/**
 * WT-5a (fieldiq-api#1254) — the display rules for Well Testing invoices: the
 * status skin, which actions a status allows, the one-group selection rule of
 * "New invoice", the tax fallback, which live invoice holds a ticket, and every
 * refusal the invoice doors answer with, as a sentence that names what it
 * carries.
 *
 * Carried over from Pipeline & Facility (lib/pf-invoices.ts) — this vertical's
 * API mirrors PF-5a — with the well in place of the site, and no mark-paid
 * (Finance's door, WT-5b).
 *
 * THE API DECIDES. Nothing here adds a line up or computes tax: the subtotal,
 * each levied tax and the total are the API's, stored at generation. The role
 * lists are the ticket lists (fieldiq-api lib/wt-roles.ts WT_TICKET_READ /
 * WT_TICKET_WRITE, which the invoice routes reuse) for hiding buttons only.
 */
import { formatCalendarDate } from './calendar-date';
import { fmtTaxRate } from './display-labels';
import { wtRefusal, WT_UNKNOWN_SKIN, type WtRefusalOverrides, type WtSkin } from './well-testing';
import { canReadWtTickets, canWriteWtTickets } from './wt-tickets';
import type { WtBillableGroup, WtInvoice, WtInvoiceLine } from '../types/wt-invoices';

// ─── Who may press what ──────────────────────────────────────────────────────

/** Reads: admin, office_staff, supervisor, dispatcher — the people who read ticket prices. A field hand does not. */
export const canReadWtInvoices = canReadWtTickets;
/** Generate, send, void, and who a send addresses: the office. */
export const canWriteWtInvoices = canWriteWtTickets;

// ─── Status ──────────────────────────────────────────────────────────────────

/**
 * The same hues as the PF, NDT and PT invoices, so an invoice reads the same
 * in every vertical: draft amber (not yet with the customer), sent and
 * approved blue (with the customer), paid green, void slate.
 */
export const WT_INVOICE_STATUS_SKIN: Record<string, WtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wtInvoiceStatusSkin = (status: string | null | undefined): WtSkin =>
  status && Object.prototype.hasOwnProperty.call(WT_INVOICE_STATUS_SKIN, status) ? WT_INVOICE_STATUS_SKIN[status] : WT_UNKNOWN_SKIN;

/** Paid and void are final: the page offers the PDF and nothing else. */
export const wtInvoiceIsFinal = (status: string | null | undefined) => status === 'paid' || status === 'void';
/** Send and void: anything not final. An unknown status offers nothing. */
export const wtInvoiceIsLive = (status: string | null | undefined) => status === 'draft' || status === 'sent' || status === 'approved';

// ─── Tax ─────────────────────────────────────────────────────────────────────

/**
 * The fallbacks the send refuses with `tax_fallback_blocked` (fieldiq-api
 * lib/company-tax.ts invoiceTaxFallbackBlocked): the tenant has rates per
 * province and this invoice fell outside them. `no_rows` and
 * `jurisdiction_unknown` are company-settings tenants and send normally.
 */
export const WT_SEND_BLOCKING_FALLBACK = ['jurisdiction_absent', 'not_yet_effective'];

/** The company's default settings priced the tax rather than a rate for the province. */
export const wtInvoiceIsTaxFallback = (inv: Pick<WtInvoice, 'taxRateSource' | 'taxFallbackReason'>) =>
  inv.taxRateSource === 'company_columns' && !!inv.taxFallbackReason;

export const wtInvoiceSendBlocked = (inv: Pick<WtInvoice, 'taxFallbackReason' | 'taxExempt'>) =>
  WT_SEND_BLOCKING_FALLBACK.includes(inv.taxFallbackReason ?? '') && !inv.taxExempt;

/** Why the company default priced this invoice, in one sentence, from the reason stored at generation. Null when there was no fallback. */
export function wtTaxFallbackText(inv: Pick<WtInvoice, 'taxRateSource' | 'taxFallbackReason' | 'jurisdiction' | 'taxExempt'>): string | null {
  if (!wtInvoiceIsTaxFallback(inv)) return null;
  const where = inv.jurisdiction || 'this province or state';
  switch (inv.taxFallbackReason) {
    case 'jurisdiction_absent':
      return `No tax rate is set up for ${where}, so the company's default tax settings priced this invoice — a different jurisdiction's rate. It cannot be sent. Add a ${where} rate in Settings → Regional & Tax, then void this invoice and create it again.`;
    case 'not_yet_effective':
      return `Every ${where} tax rate on file starts after the invoice date, so the company's default tax settings priced this invoice. It cannot be sent. Back-date a ${where} rate in Settings → Regional & Tax, then void this invoice and create it again.`;
    case 'jurisdiction_unknown':
      return "The field tickets carry no province or state, so the company's home province priced the tax. Check the well's province if that is not where the work was done.";
    case 'no_rows':
      return "The tax came from the company's tax settings (no rates are set per province).";
    default:
      return "The company's default tax settings priced this invoice.";
  }
}

/** The levied tax rows: a stored label with an amount. A 0% row is not printed. */
export function wtLeviedTaxRows(inv: Pick<WtInvoice, 'taxRate1Label' | 'taxRate1Percent' | 'taxRate1Amount' | 'taxRate2Label' | 'taxRate2Percent' | 'taxRate2Amount'>) {
  return [
    { label: inv.taxRate1Label, percent: inv.taxRate1Percent, amount: inv.taxRate1Amount },
    { label: inv.taxRate2Label, percent: inv.taxRate2Percent, amount: inv.taxRate2Amount },
  ].filter((r) => !!r.label && r.amount !== null && r.amount !== undefined && !(r.percent !== null && r.percent !== undefined && r.percent <= 0));
}

/** "GST (5%)" from the stored label and percent. */
export function wtTaxRowLabel(label: string | null | undefined, percent: number | null | undefined): string {
  if (!label) return '—';
  return percent === null || percent === undefined ? label : `${label} (${fmtTaxRate(percent)})`;
}

/** Where the tax came from, from the provenance stored at generation. Unknown → `—`. */
export function wtTaxSourceText(inv: Pick<WtInvoice, 'taxRateSource' | 'taxFallbackReason' | 'jurisdiction' | 'taxRateEffectiveFrom' | 'taxExempt'>): string {
  if (inv.taxExempt) return 'Customer is tax exempt';
  const where = inv.jurisdiction || 'the province';
  switch (inv.taxRateSource) {
    case 'jurisdiction_row': {
      const from = formatCalendarDate(inv.taxRateEffectiveFrom);
      return from ? `${where} rate, in effect from ${from}` : `${where} rate`;
    }
    case 'company_columns':
      return inv.taxFallbackReason === 'jurisdiction_absent' || inv.taxFallbackReason === 'not_yet_effective'
        ? `Company default — no ${where} rate in effect`
        : 'Company tax settings';
    case 'none':
      return 'No tax applies';
    default:
      return '—';
  }
}

// ─── Lines and tickets ───────────────────────────────────────────────────────

/** The invoice's lines, in the API's order, under the ticket each came from. */
export function wtInvoiceLineGroups(inv: Pick<WtInvoice, 'lines'>) {
  const groups: { ticketId: string; ticketNumber: string; lines: WtInvoiceLine[] }[] = [];
  for (const l of inv.lines) {
    let g = groups.find((x) => x.ticketId === l.ticketId);
    if (!g) {
      g = { ticketId: l.ticketId, ticketNumber: l.ticketNumber, lines: [] };
      groups.push(g);
    }
    g.lines.push(l);
  }
  return groups;
}

/** The ticket numbers a void released, in the invoice's ticket order. An id the invoice does not list is shown as-is. */
export function wtReleasedTicketNumbers(inv: Pick<WtInvoice, 'tickets'> | null, releasedIds: readonly string[]): string[] {
  return releasedIds.map((id) => inv?.tickets.find((t) => t.id === id)?.ticketNumber ?? id);
}

/** "WTT-0001", "WTT-0001 and WTT-0002", "WTT-0001, WTT-0002 and WTT-0003". */
export function wtListWords(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * Which live invoice holds a ticket. The ticket payload carries no invoice
 * field (an API gap, as on PF), so the ticket page DERIVES it: of the
 * customer's invoices that are not void, the one listing this ticket and not
 * having released it. null when none does.
 */
export function wtHoldingInvoice<T extends Pick<WtInvoice, 'status' | 'tickets'>>(invoices: readonly (T | null | undefined)[], ticketId: string): T | null {
  for (const inv of invoices) {
    if (!inv || inv.status === 'void') continue;
    if (inv.tickets.some((t) => t.id === ticketId && !t.releasedAt)) return inv;
  }
  return null;
}

// ─── New invoice: one group at a time ────────────────────────────────────────

/** The set one invoice may bill — the API's grouping key. */
export const wtBillableGroupKey = (g: Pick<WtBillableGroup, 'customerId' | 'provinceState' | 'currency'>) =>
  `${g.customerId}|${g.provinceState ?? ''}|${g.currency ?? ''}`;

/**
 * THE SELECTION RULE. A pick lives in exactly one group: the API refuses a
 * selection that crosses customers, provinces or currencies, so the screen
 * never builds one. Ticking a ticket in another group is not offered (its box
 * is disabled while this group holds a pick); clearing the last pick frees
 * every group again.
 */
export interface WtInvoicePick {
  groupKey: string | null;
  ids: string[];
}
export const WT_NO_PICK: WtInvoicePick = { groupKey: null, ids: [] };

export function wtTogglePick(pick: WtInvoicePick, groupKey: string, ticketId: string): WtInvoicePick {
  if (pick.groupKey !== null && pick.groupKey !== groupKey) return pick;
  const ids = pick.ids.includes(ticketId) ? pick.ids.filter((x) => x !== ticketId) : [...pick.ids, ticketId];
  return ids.length ? { groupKey, ids } : WT_NO_PICK;
}

/** Whether a group's boxes may be ticked: no pick yet, or the pick is in this group. */
export const wtGroupPickable = (pick: WtInvoicePick, groupKey: string) => pick.groupKey === null || pick.groupKey === groupKey;

/** Drop picks that are no longer billable (invoiced elsewhere, a refresh). */
export function wtPrunePick(pick: WtInvoicePick, groups: readonly WtBillableGroup[]): WtInvoicePick {
  if (pick.groupKey === null) return pick;
  const g = groups.find((x) => wtBillableGroupKey(x) === pick.groupKey);
  const live = new Set(g?.tickets.map((t) => t.ticketId) ?? []);
  const ids = pick.ids.filter((id) => live.has(id));
  return ids.length ? { groupKey: pick.groupKey, ids } : WT_NO_PICK;
}

// ─── Refusals ────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : []);

/**
 * Every `code` the WT-5a invoice doors answer with (routes/well-testing-
 * invoices.routes.ts, lib/wt-invoice.ts, the shared customer-exemption
 * refusal), as a sentence. Where the refusal carries names — ticket numbers,
 * provinces, invoice numbers — the sentence says them. The tests pin this list,
 * so a code added there and not here fails a test.
 *
 * `ticketNumber` turns a ticket id the refusal carries back into its number
 * (the screen knows the tickets it offered).
 */
function invoiceRefusals(ticketNumber: (id: string) => string): WtRefusalOverrides {
  return {
    // generate
    selection_required: () => 'Choose a job, or pick the field tickets to invoice.',
    invalid_id: () => 'That job could not be found. Refresh the page and try again.',
    tickets_required: () => 'Choose between 1 and 100 field tickets to invoice.',
    ticket_listed_twice: () => 'A field ticket is listed twice. Choose each ticket once.',
    date_invalid: () => 'The due date is not a valid date.',
    text_too_long: (x) => str(x.error) ?? 'That text is too long.',
    ticket_already_invoiced: (x) => {
      const tickets = strs(x.ticket_ids).map(ticketNumber);
      const invoices = strs(x.invoice_numbers);
      const what = tickets.length ? `${wtListWords(tickets)} ${tickets.length === 1 ? 'is' : 'are'}` : 'A ticket is';
      const on = invoices.length ? wtListWords(invoices) : 'another invoice';
      return `${what} already on ${on}. Void that invoice to bill ${tickets.length > 1 ? 'them' : 'it'} again.`;
    },
    ticket_not_customer_approved: (x) => {
      const rows = Array.isArray(x.tickets) ? (x.tickets as unknown[]) : [];
      const names = rows
        .map((t) => (t && typeof t === 'object' ? str((t as { ticket_number?: unknown }).ticket_number) : null))
        .filter((n): n is string => !!n);
      const what = names.length ? `${wtListWords(names)} ${names.length === 1 ? 'is' : 'are'}` : 'A ticket is';
      return `${what} not approved by the customer yet. Only a ticket the customer has approved can be invoiced.`;
    },
    tickets_different_customers: () => 'These tickets belong to different customers. One invoice can only bill one customer.',
    tickets_mixed_currency: (x) => str(x.error) ?? 'These tickets are in more than one currency. One invoice can only use one currency.',
    tickets_different_jurisdictions: (x) => {
      const raw = Array.isArray(x.jurisdictions) ? (x.jurisdictions as unknown[]) : [];
      const places = raw.map((p) => (typeof p === 'string' && p ? p : 'not set'));
      return places.length
        ? `These tickets were worked in different provinces or states (${wtListWords(places)}). One invoice can only cover one province or state.`
        : 'These tickets were worked in different provinces or states. One invoice can only cover one province or state.';
    },
    tax_unconfigured: (x) => str(x.error) ?? 'Tax is not set up for this province or state. Set it in Settings → Regional & Tax.',
    no_billable_tickets: (x) => str(x.error) ?? 'This job has no customer-approved field tickets waiting to be invoiced.',
    FULL_TAX_EXEMPTION_REFUSED: (x) => str(x.error) ?? 'This customer is marked fully tax exempt, which an invoice cannot carry. Check the customer record.',
    PST_EXEMPTION_NUMBER_REQUIRED: (x) => str(x.error) ?? 'This customer is PST exempt but has no exemption certificate number. Add it on the customer record.',
    invoice_number_contended: () => 'Another invoice was being numbered at the same moment. Try again.',
    invoice_state_changed: () => 'This invoice changed while this was being saved. Refresh and try again.',
    // send
    to_invalid: (x) => str(x.error) ?? 'That is not a valid email address.',
    cc_invalid: (x) => str(x.error) ?? 'One of the CC addresses is not valid.',
    invoice_not_sendable: (x) => str(x.error) ?? 'This invoice is void or already paid, so it cannot be sent.',
    tax_fallback_blocked: (x) => str(x.error) ?? "This invoice was taxed at the company's default rate because the province's rate is not set up, so it cannot be sent.",
    no_billing_email: (x) => str(x.error) ?? 'The customer has no accounting or main contact email. Type an address to send to.',
    email_not_sent: (x) => str(x.error) ?? 'The email was not sent. The invoice is unchanged.',
    // void
    reason_required: () => 'A reason is required to void an invoice.',
    invoice_already_void: () => 'This invoice is already void.',
    invoice_not_voidable: () => 'A paid invoice cannot be voided.',
    // any door
    body_required: () => 'Nothing was sent. Fill in the form and try again.',
    unknown_field: () => 'This form sent a detail the system does not recognize. Refresh the page and try again.',
    invalid_request: (x) => str(x.error) ?? 'The request was not valid.',
    not_found: () => 'This invoice or one of its tickets was not found. It may have been deleted.',
  };
}

export const WT_INVOICE_REFUSAL_CODES = Object.keys(invoiceRefusals((id) => id));

/** A refused invoice write as one sentence. A 404 is "not found", a code-less 403 a refusal of permission. */
export function wtInvoiceRefusal(err: unknown, fallback: string, ticketNumber: (id: string) => string = (id) => id): string {
  const map = invoiceRefusals(ticketNumber);
  const r = (err as { response?: { status?: unknown; data?: unknown } })?.response;
  const d = (r?.data && typeof r.data === 'object' ? r.data : {}) as Record<string, unknown>;
  if (r?.status === 404 && typeof d.code !== 'string') return map.not_found(d);
  return wtRefusal(err, fallback, map);
}

/**
 * The review or portal link a send refusal carries, if the API ever sends one
 * (fieldiq-api#1254's 502 `email_not_sent` does not today). Read from the top
 * level, where a refusal spreads its extras, and from a nested `data`.
 */
export function wtInvoiceReviewUrl(err: unknown): string | null {
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  if (!body || typeof body !== 'object') return null;
  const top = body as Record<string, unknown>;
  const nested = (top.data && typeof top.data === 'object' ? top.data : {}) as Record<string, unknown>;
  for (const url of [top.reviewUrl, top.portalUrl, nested.reviewUrl, nested.portalUrl]) {
    if (typeof url === 'string' && url) return url;
  }
  return null;
}
